import { Badge, Caption1, makeStyles, tokens } from "@fluentui/react-components";
import { Warning16Filled } from "@fluentui/react-icons";
import type { Richiesta } from "../data/richieste";
import { useData } from "../data/DataProvider";
import { useCatalogo } from "../data/catalogo";
import { formatDateTime, formatEuro, formatQuantita } from "../lib/format";
import { InfoRow, Section } from "./Section";
import { StatoBadge } from "./StatoBadge";

const useStyles = makeStyles({
  head: { display: "flex", flexDirection: "column", gap: tokens.spacingVerticalXS },
  title: { margin: 0, fontSize: tokens.fontSizeBase500, fontWeight: tokens.fontWeightSemibold, lineHeight: tokens.lineHeightBase500 },
  line: { display: "flex", alignItems: "center", gap: tokens.spacingHorizontalS, flexWrap: "wrap" },
  urgente: { display: "inline-flex", alignItems: "center", gap: "4px", color: tokens.colorPaletteRedForeground1, fontWeight: tokens.fontWeightSemibold },
  muted: { color: tokens.colorNeutralForeground3 },
  pre: { whiteSpace: "pre-wrap" },
});

/** Parte in sola lettura di una richiesta: comune a richiedente e gestore. */
export function RichiestaInfo({ r, showStato = true }: { r: Richiesta; showStato?: boolean }) {
  const s = useStyles();
  const data = useData();
  const { catalogo } = useCatalogo(data);
  const f = r.fields;
  const stampante = f.Stampante != null ? catalogo?.stampanti.find((x) => x.id === f.Stampante) : undefined;

  return (
    <>
      <div className={s.head}>
        <h1 className={s.title}>{f.Title}</h1>
        <div className={s.line}>
          {showStato && <StatoBadge stato={f.Stato} size="large" />}
          {f.Priorita === "Urgente" && (
            <span className={s.urgente}>
              <Warning16Filled /> Urgente
            </span>
          )}
          {f.ExtraBudget && (
            <Badge appearance="tint" color="warning" shape="rounded">
              Extra budget
            </Badge>
          )}
          <Caption1 className={s.muted}>
            #{r.id} · {formatDateTime(r.createdAt)}
          </Caption1>
        </div>
      </div>
      <Section>
        <InfoRow label="Richiedente">{r.createdBy?.displayName ?? "—"}</InfoRow>
        <InfoRow label="Tipo">{f.Tipo ?? "—"}</InfoRow>
        <InfoRow label="Servizio">{f.Servizio ?? "—"}</InfoRow>
        {f.Tipo === "Stampanti" ? (
          <>
            <InfoRow label="Stampante">{stampante?.nome ?? (f.Stampante != null ? `#${f.Stampante}` : "—")}</InfoRow>
            {f.Codice && <InfoRow label="Codice">{f.Codice}</InfoRow>}
          </>
        ) : (
          <InfoRow label="Categoria">{f.Categoria ?? "—"}</InfoRow>
        )}
        <InfoRow label="Descrizione">
          <span className={s.pre}>{f.Descrizione ?? "—"}</span>
        </InfoRow>
        <InfoRow label="Quantità">{formatQuantita(f.Quantita)}</InfoRow>
        {f.PrezzoUnitario != null && <InfoRow label="Prezzo unitario">{formatEuro(f.PrezzoUnitario)} (IVA esclusa)</InfoRow>}
        {f.Valore != null && <InfoRow label="Valore">{formatEuro(f.Valore)} (IVA esclusa)</InfoRow>}
        {f.Note && (
          <InfoRow label="Note">
            <span className={s.pre}>{f.Note}</span>
          </InfoRow>
        )}
      </Section>
    </>
  );
}
